import type { RouteLocationNormalized, RouteRecordRaw } from "vue-router";

import Agenda from "@/components/agenda/Agenda.vue";
import Contacts from "@/components/contacts/Contacts.vue";
import Inbox from "@/components/inbox/Inbox.vue";
import Meetings from "@/components/meetings/Meetings.vue";
import NotFound from "@/components/NotFound.vue";
import Planner from "@/components/planner/Planner.vue";
import Root from "@/components/Root.vue";
import SettingsAccount from "@/components/settings/Account.vue";
import SettingsApps from "@/components/settings/Apps.vue";
import SettingsCalendars from "@/components/settings/Calendars.vue";
import SettingsCustomMcpClients from "@/components/settings/CustomMCPClients.vue";
import SettingsDashboard from "@/components/settings/Dashboard.vue";
import SettingsInfo from "@/components/settings/Info.vue";
import Integrations from "@/components/settings/Integrations.vue";
import SettingsIntegrationGmail from "@/components/settings/IntegrationsGmail.vue";
import SettingsGmailConsent from "@/components/settings/IntegrationsGmailGrant.vue";
import IntegrationsGoogleFeature from "@/components/settings/IntegrationsGoogleFeature.vue";
import IntegrationsGoogleWorkspace from "@/components/settings/IntegrationsGoogleWorkspace.vue";
import IntegrationsNotion from "@/components/settings/IntegrationsNotion.vue";
import IntegrationsNotionSetup from "@/components/settings/IntegrationsNotionSetup.vue";
import SettingsInvite from "@/components/settings/Invite.vue";
import SettingsLogs from "@/components/settings/Logs.vue";
import McpIntegration from "@/components/settings/McpIntegration.vue";
import SettingsMeetings from "@/components/settings/Meetings.vue";
import MembraneAddDataCollection from "@/components/settings/MembraneAddDataCollection.vue";
import MembraneIntegration from "@/components/settings/MembraneIntegration.vue";
import SettingsMenu from "@/components/settings/Menu.vue";
import SettingsWorkspaceTaskBindings from "@/components/settings/OrganizationTaskBindings.vue";
import SettingsPeople from "@/components/settings/People.vue";
import SettingsPermissions from "@/components/settings/Permissions.vue";
import SettingsPlugins from "@/components/settings/Plugins.vue";
import SettingsPreferences from "@/components/settings/Preferences.vue";
import Settings from "@/components/settings/Settings.vue";
import SettingsTaskBindings from "@/components/settings/TaskBindings.vue";
import SettingsTypeEdition from "@/components/settings/TypeEdition.vue";
import SettingsTypes from "@/components/settings/Types.vue";
import SettingsWorkspace from "@/components/settings/Workspace.vue";
import SettingsWorkspaces from "@/components/settings/Workspaces.vue";
import Tasks from "@/components/tasks/Tasks.vue";
import AgentRoute from "@/routes/Agent.vue";
import AgentConfigurationRoute from "@/routes/AgentConfiguration.vue";
import AgentConversationRoute from "@/routes/AgentConversation.vue";
import AssistantRoute from "@/routes/Assistant.vue";
import AutomationRoute from "@/routes/Automation.vue";
import AutomationRunRoute from "@/routes/AutomationRun.vue";
import CanvasRoute from "@/routes/Canvas.vue";
import CustomObject from "@/routes/CustomObject.vue";
import Event from "@/routes/Event.vue";
import InstallRoute from "@/routes/Install.vue";
import Journal from "@/routes/Journal.vue";
import Oauth from "@/routes/Oauth.vue";
import Page from "@/routes/Page.vue";
import Pages from "@/routes/Pages.vue";
import Person from "@/routes/Person.vue";
import RecurrentTask from "@/routes/RecurrentTask.vue";
import TaskComponent from "@/routes/Task.vue";
import { RouteNames } from "@/routes/types";
import Database from "@/screens/Database.vue";
import Label from "@/screens/Label.vue";
import Labels from "@/screens/Labels.vue";
import Organization from "@/screens/Organization.vue";
import Projects from "@/screens/Projects.vue";
import SettingsAgents from "@/screens/settings/organization/Agents.vue";
import SettingsAutomations from "@/screens/settings/organization/Automations.vue";
import SettingsStudio from "@/screens/settings/organization/Studio.vue";
import SettingsPlan from "@/screens/settings/Plan.vue";
import Workspace from "@/screens/Workspace.vue";

import { updatePerson } from "./middlewares";

export const routes: RouteRecordRaw[] = [
  {
    path: "/",
    name: RouteNames.Root,
    component: Root,
  },
  {
    name: RouteNames.Organization,
    path: "/organizations/:orgId",
    component: Organization,
    props: true,
  },
  // App screens shown in organization menu sections.
  {
    name: RouteNames.OrgTasks,
    path: "/organizations/:orgId/tasks",
    component: Tasks,
    props: true,
  },
  {
    name: RouteNames.OrgTask,
    path: "/organizations/:orgId/tasks/:taskId",
    component: TaskComponent,
    props: (route: RouteLocationNormalized) => ({
      id: route.params.taskId,
    }),
  },
  {
    name: RouteNames.OrgProjects,
    path: "/organizations/:orgId/projects",
    component: Projects,
    props: true,
  },
  {
    name: RouteNames.OrgJournal,
    path: "/organizations/:orgId/journal",
    component: Journal,
    props: true,
  },
  {
    name: RouteNames.OrgContacts,
    path: "/organizations/:orgId/contacts",
    component: Contacts,
    props: true,
  },
  {
    name: RouteNames.OrgLabels,
    path: "/organizations/:orgId/labels",
    component: Labels,
    props: true,
  },
  {
    name: RouteNames.OrgPages,
    path: "/organizations/:orgId/pages",
    component: Pages,
    props: (route: RouteLocationNormalized) => ({
      orgId: route.params.orgId,
      pageId: Array.isArray(route.query.id)
        ? route.query.id[0]
        : (route.query.id ?? undefined),
    }),
  },
  {
    name: RouteNames.OrgPage,
    path: "/organizations/:orgId/pages/:pageId",
    component: Page,
    props: (route: RouteLocationNormalized) => ({
      id: route.params.pageId,
    }),
  },
  {
    name: RouteNames.Workspace,
    path: "/workspaces/:orgId/:workspaceId",
    component: Workspace,
    props: true,
  },
  {
    name: RouteNames.Agenda,
    path: "/agenda",
    component: Agenda,
  },
  {
    name: RouteNames.Calendar,
    path: "/calendar",
    component: Planner,
  },
  {
    name: RouteNames.Inbox,
    path: "/inbox",
    component: Inbox,
  },
  {
    name: RouteNames.Journal,
    path: "/journal",
    component: Journal,
  },
  {
    name: RouteNames.Meetings,
    path: "/meetings",
    component: Meetings,
  },
  {
    name: RouteNames.Labels,
    path: "/labels",
    component: Labels,
  },
  {
    name: RouteNames.Label,
    path: "/labels/:labelId",
    component: Label,
    props: true,
  },
  {
    name: RouteNames.Install,
    path: "/install",
    component: InstallRoute,
  },
  {
    name: RouteNames.Assistant,
    path: "/assistant",
    component: AssistantRoute,
  },
  {
    name: RouteNames.Agent,
    path: "/agents/:agentId",
    component: AgentRoute,
    props: true,
  },
  {
    name: RouteNames.AgentConversation,
    path: "/agents/:agentId/conversations",
    component: AgentConversationRoute,
    props: true,
  },
  {
    name: RouteNames.AgentConfiguration,
    path: "/agents/:agentId/configuration",
    component: AgentConfigurationRoute,
    props: true,
  },
  {
    name: RouteNames.Canvas,
    path: "/canvases/:orgId/:workspaceId/:rowId",
    component: CanvasRoute,
    props: true,
  },
  {
    name: RouteNames.Automation,
    path: "/automations/:objectId",
    component: AutomationRoute,
    props: true,
  },
  {
    name: RouteNames.AutomationRunDetail,
    path: "/automations/:objectId/runs/:runId",
    component: AutomationRunRoute,
    props: true,
  },
  {
    name: RouteNames.Contacts,
    path: "/contacts",
    component: Contacts,
  },
  {
    name: RouteNames.RecurrentTask,
    path: "/recurrent-tasks/:id",
    component: RecurrentTask,
    props: true,
  },
  {
    path: "/settings",
    component: Settings,
    children: [
      {
        path: "/settings",
        name: RouteNames.Settings,
        redirect: "/settings/preferences",
      },
      {
        path: "/settings/calendars",
        name: RouteNames.SettingsCalendars,
        component: SettingsCalendars,
      },
      {
        path: "/settings/meetings",
        name: RouteNames.SettingsMeetings,
        component: SettingsMeetings,
      },
      {
        path: "/settings/preferences",
        name: RouteNames.SettingsPreferences,
        component: SettingsPreferences,
      },
      {
        path: "/settings/menu",
        name: RouteNames.SettingsMenu,
        component: SettingsMenu,
      },
      {
        path: "/settings/logs",
        name: RouteNames.SettingsLogs,
        component: SettingsLogs,
      },
      {
        path: "/settings/account",
        name: RouteNames.SettingsAccount,
        component: SettingsAccount,
      },
      {
        path: "/settings/advanced",
        redirect: "/settings/account",
      },
      {
        path: "/settings/task-bindings",
        name: RouteNames.SettingsTaskBindings,
        component: SettingsTaskBindings,
      },
      {
        path: "/settings/permissions",
        name: RouteNames.SettingsPermissions,
        component: SettingsPermissions,
      },
      // Organization-scoped integration routes
      {
        path: "/settings/organizations/:orgId/integrations",
        name: RouteNames.SettingsIntegrations,
        component: Integrations,
        props: true,
      },
      {
        path: "/settings/organizations/:orgId/integrations/notion",
        name: RouteNames.SettingsIntegrationsNotion,
        component: IntegrationsNotion,
        props: true,
      },
      {
        path: "/settings/organizations/:orgId/integrations/notion/:id",
        name: RouteNames.SettingsIntegrationsNotionSetup,
        component: IntegrationsNotionSetup,
        props: true,
      },
      {
        path: "/settings/organizations/:orgId/integrations/gmail",
        name: RouteNames.SettingsIntegrationsGmail,
        component: SettingsIntegrationGmail,
        props: true,
      },
      {
        path: "/settings/organizations/:orgId/integrations/gmail/grant/:id",
        name: RouteNames.SettingsIntegrationsGmailConsent,
        component: SettingsGmailConsent,
        props: true,
      },
      {
        path: "/settings/organizations/:orgId/integrations/google-workspace",
        name: RouteNames.SettingsIntegrationsGoogleWorkspace,
        component: IntegrationsGoogleWorkspace,
        props: true,
      },
      {
        path: "/settings/organizations/:orgId/integrations/google-calendar",
        name: RouteNames.SettingsIntegrationsGoogleCalendar,
        component: IntegrationsGoogleFeature,
        props: (route: RouteLocationNormalized) => ({
          ...route.params,
          feature: "calendar",
        }),
      },
      {
        path: "/settings/organizations/:orgId/integrations/google-contacts",
        name: RouteNames.SettingsIntegrationsGoogleContacts,
        component: IntegrationsGoogleFeature,
        props: (route: RouteLocationNormalized) => ({
          ...route.params,
          feature: "contacts",
        }),
      },
      {
        path: "/settings/organizations/:orgId/integrations/generic/:appKey",
        name: RouteNames.SettingsMembraneIntegration,
        component: MembraneIntegration,
        props: true,
      },
      {
        path: "/settings/organizations/:orgId/integrations/generic/:appKey/add-data-collection",
        name: RouteNames.SettingsMembraneAddDataCollection,
        component: MembraneAddDataCollection,
        props: true,
      },
      {
        path: "/settings/organizations/:orgId/integrations/mcp/:packageId",
        name: RouteNames.SettingsMcpIntegration,
        component: McpIntegration,
        props: true,
      },
      {
        path: "/settings/organization/:orgId/workspace/:workspaceId/types",
        name: RouteNames.SettingsTypes,
        component: SettingsTypes,
        props: true,
      },
      {
        path: "/settings/organization/:orgId/workspace/:workspaceId/task-bindings",
        name: RouteNames.SettingsWorkspaceTaskBindings,
        component: SettingsWorkspaceTaskBindings,
        props: true,
      },
      {
        path: "/settings/organization/:orgId/workspace/:workspaceId/type/:id",
        name: RouteNames.SettingsTypeEdition,
        component: SettingsTypeEdition,
        props: true,
      },
      {
        path: "/settings/organization/:orgId/workspace/:workspaceId/type-creation",
        name: RouteNames.SettingsTypeCreation,
        component: SettingsTypeEdition,
        props: true,
      },
      {
        path: "/settings/dashboard",
        name: RouteNames.SettingsDashboard,
        component: SettingsDashboard,
      },
      {
        path: "/settings/invite",
        name: RouteNames.SettingsInvite,
        component: SettingsInvite,
      },
      {
        path: "/settings/plugins",
        name: RouteNames.SettingsPlugins,
        component: SettingsPlugins,
      },
      {
        path: "/settings/apps",
        name: RouteNames.SettingsApps,
        component: SettingsApps,
      },
      {
        path: "/settings/custom-mcp-clients",
        name: RouteNames.SettingsCustomMcpClients,
        component: SettingsCustomMcpClients,
      },

      // Organization-specific routes
      {
        path: "/settings/organizations/:orgId/organization",
        name: RouteNames.SettingsOrganization,
        component: SettingsInfo,
        props: true,
      },
      {
        path: "/settings/organizations/:orgId/plan",
        name: RouteNames.SettingsPlan,
        component: SettingsPlan,
        props: true,
      },
      {
        path: "/settings/organizations/:orgId/people",
        name: RouteNames.SettingsPeople,
        component: SettingsPeople,
        props: true,
      },
      {
        path: "/settings/organizations/:orgId/workspaces",
        name: RouteNames.SettingsWorkspaces,
        component: SettingsWorkspaces,
        props: true,
      },
      {
        path: "/settings/organizations/:orgId/agents",
        name: RouteNames.SettingsAgents,
        component: SettingsAgents,
        props: true,
      },
      {
        path: "/settings/organizations/:orgId/automations",
        name: RouteNames.SettingsAutomations,
        component: SettingsAutomations,
        props: true,
      },
      {
        path: "/settings/organizations/:orgId/studio",
        name: RouteNames.SettingsStudio,
        component: SettingsStudio,
        props: true,
      },
      {
        path: "/settings/organizations/:orgId/automations/:automationId",
        name: RouteNames.SettingsAutomation,
        component: AutomationRoute,
        props: (route: RouteLocationNormalized) => ({
          objectId: route.params.automationId,
          organizationId: route.params.orgId,
          showTopBar: false,
        }),
      },
      {
        path: "/settings/organizations/:orgId/workspace/:workspaceId",
        name: RouteNames.SettingsWorkspace,
        component: SettingsWorkspace,
        props: true,
      },
    ],
  },
  {
    name: RouteNames.Event,
    path: "/events/:id",
    component: Event,
    props: true,
  },
  {
    name: RouteNames.Person,
    path: "/people/:id",
    component: Person,
    beforeEnter: updatePerson,
    props: true,
  },
  {
    name: RouteNames.Database,
    path: "/databases/:workspaceId/:databaseId",
    component: Database,
    props: true,
  },
  {
    name: RouteNames.Object,
    path: "/objects/:workspaceId/:tableId/:rowId",
    component: CustomObject,
    props: true,
  },
  {
    name: "oauth",
    path: "/oauth",
    component: Oauth,
  },
  {
    name: RouteNames.Signin,
    path: "/signin",
    component: () => import("@/components/Root.vue"),
  },
  {
    name: RouteNames.Signup,
    path: "/signup",
    component: () => import("@/components/Root.vue"),
  },
  {
    name: RouteNames.NotFound,
    path: "/:pathMatch(.*)*",
    component: NotFound,
  },
];
